import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';
import SafeIcon from '../../common/SafeIcon';

const { FiTarget, FiUser, FiZap, FiBook } = FiIcons;

const ModuleOne = ({ data, onSave, completed }) => {
  const [formData, setFormData] = useState({
    callingPrompt: '',
    burdenNudge: '',
    readerName: '',
    readerStruggle: '',
    readerLonging: '',
    spiritualTransformation: '',
    emotionalTransformation: '',
    practicalTransformation: '',
    biblicalAnchor: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData(prev => ({
        ...prev,
        callingPrompt: data.callingPrompt || '',
        burdenNudge: data.burdenNudge || '',
        readerName: data.readerName || '',
        readerStruggle: data.readerStruggle || '',
        readerLonging: data.readerLonging || '',
        spiritualTransformation: data.spiritualTransformation || '',
        emotionalTransformation: data.emotionalTransformation || '',
        practicalTransformation: data.practicalTransformation || '',
        biblicalAnchor: data.biblicalAnchor || ''
      }));
    }
  }, [data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateClarityStatement = () => {
    const { readerName, readerStruggle, spiritualTransformation, biblicalAnchor } = formData;
    if (readerName?.trim() && readerStruggle?.trim() && spiritualTransformation?.trim() && biblicalAnchor?.trim()) {
      return `This book exists to help ${readerName} move from ${readerStruggle} to ${spiritualTransformation} through ${biblicalAnchor}.`;
    }
    return '';
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    const clarityStatement = generateClarityStatement();
    const dataToSave = { ...formData, clarityStatement };
    
    // Smooth delay for visual feedback
    setTimeout(() => {
      onSave(dataToSave, markComplete);
      setIsLoading(false);
    }, 800);
  };

  // Safe check for completion - ensuring all fields are present and not empty
  const requiredFields = [
    'callingPrompt', 'burdenNudge', 'readerName', 'readerStruggle', 
    'readerLonging', 'spiritualTransformation', 'emotionalTransformation', 
    'practicalTransformation', 'biblicalAnchor'
  ];
  
  const canComplete = requiredFields.every(field => 
    formData[field] && typeof formData[field] === 'string' && formData[field].trim() !== ''
  );

  const clarityStatement = generateClarityStatement();

  return (
    <ModuleLayout
      title="Clarify the Calling"
      description="Discern why this book exists, who it's for, and the transformation God intends it to bring."
      icon={FiTarget}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Core Calling */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <SafeIcon icon={FiTarget} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">The Calling</h3>
          </div>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                What is God calling you to write? Why does this book matter now?
              </label>
              <textarea
                value={formData.callingPrompt}
                onChange={(e) => handleInputChange('callingPrompt', e.target.value)}
                rows={4}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                placeholder="Share your heart about this calling..."
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                What message, question, or tension won't leave you alone?
              </label>
              <textarea
                value={formData.burdenNudge}
                onChange={(e) => handleInputChange('burdenNudge', e.target.value)}
                rows={4}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                placeholder="The 'burden' or 'nudge' you feel..."
              />
            </div>
          </div>
        </div>

        {/* Reader Snapshot */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
              <SafeIcon icon={FiUser} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Reader Snapshot</h3>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Reader name or description
                </label>
                <input
                  type="text"
                  value={formData.readerName}
                  onChange={(e) => handleInputChange('readerName', e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                  placeholder="e.g., Struggling new mom"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Their current struggle
                </label>
                <input
                  type="text"
                  value={formData.readerStruggle}
                  onChange={(e) => handleInputChange('readerStruggle', e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                  placeholder="What pain are they in?"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                Their deepest longing
              </label>
              <input
                type="text"
                value={formData.readerLonging}
                onChange={(e) => handleInputChange('readerLonging', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="What do they heart-deep desire?"
              />
            </div>
          </div>
        </div>

        {/* Transformation Outcomes */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-green-50 text-green-600 rounded-lg">
              <SafeIcon icon={FiZap} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Transformation Outcomes</h3>
          </div>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Spiritual Transformation</label>
              <input
                type="text"
                value={formData.spiritualTransformation}
                onChange={(e) => handleInputChange('spiritualTransformation', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="How does their relationship with God change?"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Emotional Transformation</label>
              <input
                type="text"
                value={formData.emotionalTransformation}
                onChange={(e) => handleInputChange('emotionalTransformation', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="How do their feelings or internal state change?"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Practical Transformation</label>
              <input
                type="text"
                value={formData.practicalTransformation}
                onChange={(e) => handleInputChange('practicalTransformation', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="What changes in their daily life or actions?"
              />
            </div>
          </div>
        </div>

        {/* Biblical Anchor */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <SafeIcon icon={FiBook} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Biblical Anchor</h3>
          </div>
          <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
            Scripture, theme, or gospel truth grounding this book
          </label>
          <textarea
            value={formData.biblicalAnchor}
            onChange={(e) => handleInputChange('biblicalAnchor', e.target.value)}
            rows={3}
            className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
            placeholder="What truth anchors your message?"
          />
        </div>

        {/* Clarity Statement */}
        {clarityStatement && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-[#2558ab] to-[#3a7bd5] rounded-3xl p-8 text-white shadow-xl shadow-blue-100"
          >
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <SafeIcon icon={FiTarget} className="mr-2" />
              Your Clarity Statement
            </h3>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <p className="text-xl font-medium italic leading-relaxed">
                "{clarityStatement}"
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default ModuleOne;